import { useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useSession } from '../auth/session.tsx';
import { ApiError } from '../api/client.ts';
import {
  fetchItineraryFacility, useItineraryAllowed, useItineraryFacilities, useItineraryStaff,
  type ItineraryClient, type ItineraryFacilityDetail,
} from '../api/itinerary.ts';
import { checklistHtml, stopChecklist } from '../itineraryPrint.ts';
import { PinMap, type Pin, type PinStyle } from '../components/PinMap.tsx';
import { BuildingPanel, EveryoneToggle, type PanelState } from '../components/BuildingPanel.ts';
import { tokenColour } from '../tokens.ts';
import {
  itineraryPins, readView, saveView, stopNumber, unmappedLine, withEveryone, withStop, withoutStop, type ItineraryView,
} from '../itineraryMap.ts';

// The visits board's map colours: a building with clients due in its "behind" colour, an added stop in the brand.
const pinStyles = (): Record<string, PinStyle> => ({
  due: { label: 'Clients due', fill: tokenColour('pace-low'), edge: tokenColour('pin-edge'), radius: 9 },
  stop: { label: 'Added, by stop number', fill: tokenColour('brand'), edge: tokenColour('pin-edge'), radius: 13 },
});

/**
 * The visit itinerary, first half (Arthur, 20 October): pick a date and a
 * worker, add buildings, reorder and remove clients, and print a checklist.
 * Results entry is parked.
 *
 * Nothing is saved. The itinerary lives in this page's memory until it is
 * printed or the page is left; a per diem worker's name is typed here and
 * never leaves the browser (no URL, no request, no storage). Which clients are
 * due is the API's answer, from v_next_visit_due, never worked out here.
 */

/** One stop. `everyone`: print its residents up to date as well as the due (A-#54); on unless set off, per stop. */
interface Group { detail: ItineraryFacilityDetail; clients: ItineraryClient[]; everyone: boolean }

function tomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/**
 * The map view at desktop width fills the window below what is above it, and the page does not scroll (Arthur,
 * 22 October, A-#41): `box` is given the height left between its top and the window's bottom, less what `page`
 * keeps below it. Under 64rem (a tablet) the map and the list stack and the page scrolls as any other.
 */
function useFitToWindow(box: RefObject<HTMLElement>, page: RefObject<HTMLElement>, on: boolean, again: unknown) {
  useLayoutEffect(() => {
    const el = box.current;
    if (!on || !el) return;
    const fit = () => {
      if (!window.matchMedia('(min-width: 64rem)').matches || !page.current) { el.style.height = ''; return; }
      const below = page.current.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom;
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(320, Math.floor(window.innerHeight - top - below))}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => { window.removeEventListener('resize', fit); el.style.height = ''; };
  }, [box, page, on, again]);
}

const btn = 'min-h-[44px] min-w-[44px] rounded-lg border border-line bg-surface px-3 font-semibold text-neutral-900 disabled:text-neutral-500';

export function VisitItinerary() {
  const allowed = useItineraryAllowed();
  const may = allowed.data?.allowed === true;
  const { getToken } = useSession();
  const [date, setDate] = useState(tomorrow);
  const [worker, setWorker] = useState('');
  const [perDiemName, setPerDiemName] = useState('');
  const [pick, setPick] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const staff = useItineraryStaff(may);
  const facilities = useItineraryFacilities(date, may);

  const visitor = worker === 'per-diem' ? perDiemName.trim() : staff.data?.staff.find((s) => s.id === worker)?.full_name ?? '';
  const stale = groups.some((g) => g.detail.date !== date);
  const rows = useMemo(() => groups.reduce((n, g) => n + stopChecklist(g).clients.length, 0), [groups]);
  // List or Map, remembered for this viewer in this browser only; List when nothing is remembered.
  const [view, setView] = useState<ItineraryView>(() => readView());
  const choose = (v: ItineraryView) => { setView(v); saveView(v); if (v === 'list') { opened.current += 1; setPanel(null); } };
  const [styles] = useState(pinStyles);
  const stops = useMemo(() => groups.map((g) => g.detail.facility.id), [groups]);
  const map = useMemo(() => itineraryPins(facilities.data?.facilities ?? [], stops), [facilities.data, stops]);
  // The building panel: opened by a pin's click, which reads that building once through the audited per-building
  // call and adds nothing. A later click wins over an earlier one still loading.
  const [panel, setPanel] = useState<PanelState | null>(null);
  // The panel's toggle before Add: on for every building opened, until set off (A-#54).
  const [panelEveryone, setPanelEveryone] = useState(true);
  const opened = useRef(0);
  const closePanel = () => { opened.current += 1; setPanel(null); };
  const pageBox = useRef<HTMLDivElement>(null);
  const columns = useRef<HTMLDivElement>(null);
  useFitToWindow(columns, pageBox, view === 'map', worker);

  if (allowed.isPending) return <p className="p-6 text-neutral-600">Loading…</p>;
  if (!may) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-bold text-neutral-900">Visit itinerary</h1>
        <p className="mt-2 text-neutral-700">Visit itineraries are open to the Director of Social Work and above.</p>
      </div>
    );
  }

  const add = async (id: string) => {
    if (!id || groups.some((g) => g.detail.facility.id === id)) return;
    setBusy(true);
    setError(null);
    try {
      const detail = await fetchItineraryFacility(getToken, id, date);
      setGroups((gs) => [...gs, { detail, clients: detail.clients, everyone: true }]);
      setPick('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The building could not be added.');
    } finally {
      setBusy(false);
    }
  };

  const openPanel = async (pin: Pin) => {
    const seq = ++opened.current;
    setPanelEveryone(true);
    setPanel({ kind: 'loading', id: pin.id, title: pin.title });
    try {
      const detail = await fetchItineraryFacility(getToken, pin.id, date);
      if (seq === opened.current) setPanel({ kind: 'ready', id: pin.id, title: detail.facility.facility_name, detail });
    } catch (e) {
      if (seq === opened.current) {
        setPanel({ kind: 'failed', id: pin.id, title: pin.title, message: e instanceof ApiError ? e.message : 'The building could not be read.' });
      }
    }
  };

  // Rebuilt for a new date, keeping the buildings and their order; clients come back as the API now answers.
  const rebuild = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await Promise.all(groups.map((g) => fetchItineraryFacility(getToken, g.detail.facility.id, date)));
      setGroups(next.map((detail, i) => ({ detail, clients: detail.clients, everyone: groups[i]?.everyone ?? true })));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The itinerary could not be rebuilt.');
    } finally {
      setBusy(false);
    }
  };

  const print = () => {
    const html = checklistHtml({
      date,
      visitor: visitor || '________________',
      facilities: groups.map(stopChecklist),
    });
    const w = window.open('', '_blank');
    if (!w) { setError('The browser blocked the checklist window. Allow pop-ups for procura and try again.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const setClients = (gi: number, clients: ItineraryClient[]) =>
    setGroups((gs) => gs.map((g, i) => (i === gi ? { ...g, clients } : g)));
  // A stop's own choice, changed after adding, from its section or from the map's panel.
  const setEveryone = (id: string, everyone: boolean) => setGroups((gs) => withEveryone(gs, id, everyone));

  // What has been added, with the date and error notes: under the controls in List, the right-hand third in Map.
  const itinerary = (
    <>
      {facilities.data && facilities.data.facilities.length === 0 && (
        <p className="mt-2 text-sm text-neutral-600">No building you can see has a client due by this date.</p>
      )}
      {stale && (
        <p role="status" className="mt-3 rounded-lg border-l-8 border-note-edge bg-note-bg px-4 py-3 font-semibold text-note-fg">
          The date has changed since these buildings were added. <button type="button" className="underline" onClick={() => void rebuild()}>Rebuild for the new date</button> before printing.
        </p>
      )}
      {error && <p role="alert" className="mt-3 font-semibold text-critical-edge">{error}</p>}

      {groups.map((g, gi) => (
        <section key={g.detail.facility.id} className="panel mt-4">
          <div className="panel-bar flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1">{g.detail.facility.facility_name}</span>
            <button type="button" className={btn} aria-label="Move building up" disabled={gi === 0} onClick={() => setGroups((gs) => move(gs, gi, gi - 1))}>↑</button>
            <button type="button" className={btn} aria-label="Move building down" disabled={gi === groups.length - 1} onClick={() => setGroups((gs) => move(gs, gi, gi + 1))}>↓</button>
            <button type="button" className={btn} onClick={() => setGroups((gs) => gs.filter((_, i) => i !== gi))}>Remove building</button>
          </div>
          <p className="px-4 pt-2 text-sm text-neutral-700">
            {[g.detail.facility.street_address, g.detail.facility.city].filter(Boolean).join(', ') || 'No address on record'}
            {' · '}{g.detail.facility.phone ?? 'No telephone on record'}
          </p>
          <div className="px-4 pt-2">
          <EveryoneToggle name={`everyone-${g.detail.facility.id}`} everyone={g.everyone} onChange={(v) => setEveryone(g.detail.facility.id, v)} />
          <p className="mt-1 text-sm text-neutral-700">
            {g.everyone
              ? `${g.detail.up_to_date.length} up to date also on the checklist, after the clients due.`
              : 'Only the clients due are on the checklist.'}
          </p>
        </div>
        {g.detail.away > 0 && (
            <p className="px-4 text-sm text-neutral-600">
              {g.detail.away} more {g.detail.away === 1 ? 'client is' : 'clients are'} due but away on a temporary stay, so not listed.
            </p>
          )}
          {g.clients.length === 0 ? (
            <p className="px-4 py-3 text-neutral-600">No client left at this building.</p>
          ) : (
            <ol className="divide-y divide-line px-4 pb-2">
              {g.clients.map((c, ci) => (
                <li key={c.client_id} className="flex min-h-[44px] flex-wrap items-center gap-2 py-1.5">
                  <span className="min-w-0 flex-1 text-neutral-900">
                    {c.name}
                    <span className="ml-2 text-sm text-neutral-600">{c.visit_type}{c.overdue ? ', overdue' : ''}</span>
                  </span>
                  <button type="button" className={btn} aria-label={`Move ${c.name} up`} disabled={ci === 0} onClick={() => setClients(gi, move(g.clients, ci, ci - 1))}>↑</button>
                  <button type="button" className={btn} aria-label={`Move ${c.name} down`} disabled={ci === g.clients.length - 1} onClick={() => setClients(gi, move(g.clients, ci, ci + 1))}>↓</button>
                  <button type="button" className={btn} aria-label={`Remove ${c.name}`} onClick={() => setClients(gi, g.clients.filter((_, i) => i !== ci))}>Remove</button>
                </li>
              ))}
            </ol>
          )}
        </section>
      ))}
    </>
  );

  return (
    <div ref={pageBox} className={view === 'list' ? 'min-h-dvh bg-page' : 'bg-page'}>
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Visit itinerary</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Pick a date and who is visiting, add buildings, and print the checklist. Each building brings every client
            placed there whose visit is due by that date. Nothing is saved: the itinerary lasts until it is printed or
            this page is left.
          </p>
        </header>
        <div className="page-body px-4 lg:px-5">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-sm font-semibold text-neutral-800">
              Date
              <input type="date" value={date} onChange={(e) => { setDate(e.target.value); closePanel(); }}
                     className="min-h-[44px] rounded-lg border border-line bg-surface px-2 font-normal" />
            </label>
            <label className="flex flex-col text-sm font-semibold text-neutral-800">
              Visitor
              <select value={worker} onChange={(e) => setWorker(e.target.value)}
                      className="min-h-[44px] rounded-lg border border-line bg-surface px-2 font-normal">
                <option value="">Choose…</option>
                <option value="per-diem">A per diem worker (type the name)</option>
                {staff.data?.staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </label>
            {worker === 'per-diem' && (
              <label className="flex flex-col text-sm font-semibold text-neutral-800">
                Per diem worker’s name
                <input type="text" value={perDiemName} onChange={(e) => setPerDiemName(e.target.value)} autoComplete="off"
                       className="min-h-[44px] rounded-lg border border-line bg-surface px-2 font-normal" />
              </label>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            {view === 'list' && (
              <>
                <label className="flex flex-col text-sm font-semibold text-neutral-800">
                  Building
                  <select value={pick} onChange={(e) => setPick(e.target.value)}
                          className="min-h-[44px] min-w-[18rem] rounded-lg border border-line bg-surface px-2 font-normal">
                    <option value="">{facilities.isPending ? 'Loading…' : 'Choose a building…'}</option>
                    {facilities.data?.facilities
                      .filter((f) => !groups.some((g) => g.detail.facility.id === f.id))
                      .map((f) => <option key={f.id} value={f.id}>{[f.facility_name, f.street_address, f.town].filter(Boolean).join(', ')} ({f.due} due)</option>)}
                  </select>
                </label>
                <button type="button" className={btn} disabled={!pick || busy} onClick={() => void add(pick)}>Add facility</button>
              </>
            )}
            <div role="group" aria-label="Choose buildings from" className="flex">
              {(['list', 'map'] as const).map((v) => (
                <button key={v} type="button" aria-pressed={view === v} onClick={() => choose(v)}
                        className={`min-h-[44px] min-w-[44px] border border-line px-4 font-semibold first:rounded-l-lg last:rounded-r-lg ${
                          view === v ? 'bg-brand text-on-brand' : 'bg-surface text-neutral-900'}`}>
                  {v === 'list' ? 'List' : 'Map'}
                </button>
              ))}
            </div>
            <button type="button" className="min-h-[44px] rounded-lg bg-brand px-4 font-semibold text-white disabled:bg-neutral-400"
                    disabled={rows === 0 || stale || busy} onClick={print}>
              Print checklist
            </button>
          </div>
          {view === 'list' ? itinerary : (
            <div ref={columns} className="mt-3 grid gap-4 pb-4 lg:grid-cols-3 lg:grid-rows-[minmax(0,1fr)]">
              <div className="relative h-[28rem] lg:col-span-2 lg:h-full">
                <PinMap pins={map.pins} styles={styles} fitKey={date} label="Map of buildings with clients due"
                        onOpen={(pin) => void openPanel(pin)} showKey fill />
                {panel && (
                  <div className="pointer-events-none absolute top-3 right-3 bottom-24 z-[1000] flex max-w-[calc(100%-1.5rem)] flex-col items-end">
                    <div className="pointer-events-auto flex max-h-full min-h-0 max-w-full">
                      <BuildingPanel state={panel} stop={stopNumber(stops, panel.id)} busy={busy} onClose={closePanel}
                                     everyone={groups.find((g) => g.detail.facility.id === panel.id)?.everyone ?? panelEveryone}
                                     onEveryone={(v) => { if (stops.includes(panel.id)) setEveryone(panel.id, v); else setPanelEveryone(v); }}
                                     onAdd={(detail) => setGroups((gs) => withStop(gs, { detail, clients: detail.clients, everyone: panelEveryone }))}
                                     onRemove={() => setGroups((gs) => withoutStop(gs, panel.id))} />
                    </div>
                  </div>
                )}
              </div>
              <div aria-label="The itinerary" role="region" className="min-h-0 min-w-0 lg:overflow-y-auto lg:pr-1">
                <p className="text-sm text-neutral-700">Click a building to see who is due there and add it. Added buildings show their stop number.</p>
                {unmappedLine(map.unmapped) && <p role="status" className="mt-2 text-sm text-neutral-700">{unmappedLine(map.unmapped)}</p>}
                {itinerary}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
